import { useState } from 'react'

import { Box, Section } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Pencil } from '@factorialco/f0-react/icons/app'
import {
  AtsRejectionReasonItemFragment,
  AtsRejectionReasonsListDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { AddRejectionReasonModal } from './AddRejectionReasonModal'
import { DeleteRejectionReasonModal } from './DeleteRejectionReasonModal'
import { UpdateRejectionReasonModal } from './UpdateRejectionReasonModal'

export const isSystemManagedRejectionReason = (item: AtsRejectionReasonItemFragment) =>
  item.systemManaged === true

export const canManageRejectionReason = (item: AtsRejectionReasonItemFragment) =>
  !isSystemManagedRejectionReason(item)

export const RejectionReasonsManager = () => {
  const [showModal, setShowModal] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [reason, setReason] = useState<AtsRejectionReasonItemFragment>()

  const rejectionReasonsSource = useGraphqlDataCollectionSource({
    document: AtsRejectionReasonsListDocument,
    mapData: (data) => data.ats.rejectionReasonsConnection.nodes,
    mapVariables: ({ sortings }) => {
      return { sort: sortings?.[0] }
    },
    mapPagination: (data) => ({
      total: data.ats.rejectionReasonsConnection.totalCount ?? 0,
    }),
    primaryActions: () => ({
      label: i18n.t('ats.rejection_reasons.settings.add_new_reason'),
      onClick: () => setShowModal(true),
      icon: Add,
    }),
    itemActions: (item) => [
      ...(canManageRejectionReason(item)
        ? [
            {
              label: i18n.t('common.delete'),
              icon: Delete,
              onClick: () => {
                setReason(item)
                setShowDeleteModal(true)
              },
            },
          ]
        : []),
      ...(canManageRejectionReason(item)
        ? [
            {
              label: i18n.t('common.edit'),
              icon: Pencil,
              onClick: () => {
                setReason(item)
                setShowUpdateModal(true)
              },
            },
          ]
        : []),
    ],
    sortings: {
      reason: {
        label: i18n.t('ats.rejection_reasons.settings.title'),
      },
      decisionMaker: {
        label: i18n.t('ats.rejection_reasons.settings.decision_maker.title'),
      },
    },
    defaultSortings: {
      field: 'reason',
      order: 'asc',
    },
  })

  const decisionMaker = (value: string) =>
    value === 'company'
      ? i18n.t('ats.rejection_reasons.settings.decision_maker.company')
      : i18n.t('ats.rejection_reasons.settings.decision_maker.candidate')

  return (
    <>
      <Section
        illustration="single-neutral-actions-process"
        title={i18n.t('ats.rejection_reasons.settings.title')}
        description={i18n.t('ats.rejection_reasons.settings.description')}
      >
        {showModal && <AddRejectionReasonModal onClose={() => setShowModal(false)} />}
        {showDeleteModal && reason && (
          <DeleteRejectionReasonModal
            rejectionReason={reason}
            onClose={() => setShowDeleteModal(false)}
          />
        )}
        {showUpdateModal && reason && (
          <UpdateRejectionReasonModal
            rejectionReason={reason}
            onClose={() => setShowUpdateModal(false)}
          />
        )}
      </Section>
      <Box paddingX="s64" paddingBottom="s32">
        <OneDataCollection
          source={rejectionReasonsSource}
          visualizations={[
            {
              type: 'table',
              options: {
                columns: [
                  {
                    label: i18n.t('ats.rejection_reasons.settings.title'),
                    sorting: 'reason',
                    render: (item) => ({
                      type: 'text',
                      value: item.reason,
                    }),
                  },
                  {
                    label: i18n.t('ats.rejection_reasons.settings.decision_maker.title'),
                    sorting: 'decisionMaker',
                    render: (item) => ({
                      type: 'text',
                      value: decisionMaker(item.decisionMaker),
                    }),
                  },
                ],
              },
            },
          ]}
        />
      </Box>
    </>
  )
}
